/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Shared date field styles for assignment and quick schedule forms.
 *
 * @file AetDateFieldStyle.jsx
 * @author Anuja Gaikwad
 * @since 2.0.0
 */

import { styled } from '@mui/material/styles';
import { Box, Button, Typography } from '@mui/material';
import mixins from '../../../assets/styles/mixins.scss';
import {
  quickScheduleFormFieldLabelStyles
} from '../../QuickSchedule/quickScheduleFormConstants';
import {
  getAssignmentDueDateCalendarHeaderStyles,
  getAssignmentDueDateCalendarStyles
} from '../../assignments/styles/assignmentDueDateCalendarStyles';

const assignmentCalendarHeaderStyles = getAssignmentDueDateCalendarHeaderStyles();

const ASSIGNMENT_FORM_DATE_FIELD = '.assignment-form-date-field';
const QUICK_SCHEDULE_FORM_DATE_FIELD = '.quick-schedule-form-date-field';

const calendarStyles = {
  '& .react-datepicker': {
    fontFamily: mixins.fontFamilyPlusSans,
    border: '1px solid #E0E0E0',
    borderRadius: 8,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    padding: 8,
    background: 'white'
  },
  '& .react-datepicker__header': {
    backgroundColor: 'white',
    borderBottom: 'none',
    paddingTop: 0,
    paddingBottom: 8
  },
  '& .react-datepicker__day-names': {
    display: 'flex',
    justifyContent: 'space-around',
    marginBottom: 4
  },
  '& .react-datepicker__day-name': {
    color: '#666',
    fontSize: 14,
    fontWeight: 500,
    width: 36,
    lineHeight: '36px',
    margin: 2
  },
  '& .react-datepicker__month': {
    margin: 0
  },
  '& .react-datepicker__week': {
    display: 'flex',
    justifyContent: 'space-around'
  },
  '& .react-datepicker__day': {
    color: '#2E2E2E',
    fontSize: 14,
    width: 36,
    height: 36,
    lineHeight: '36px',
    margin: 2,
    borderRadius: 8,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#F5F5F5',
      borderRadius: 8
    }
  },
  '& .react-datepicker__day--selected, & .react-datepicker__day--keyboard-selected': {
    backgroundColor: `${mixins.amethyst} !important`,
    color: 'white !important',
    fontWeight: 600,
    borderRadius: 8
  },
  '& .react-datepicker__day--today': {
    fontWeight: 600,
    color: mixins.amethyst,
    backgroundColor: 'transparent'
  },
  '& .react-datepicker__day--today.react-datepicker__day--selected': {
    backgroundColor: `${mixins.amethyst} !important`,
    color: 'white !important'
  },
  '& .react-datepicker__day--outside-month': {
    color: '#BDBDBD'
  },
  '& .react-datepicker__day--disabled': {
    color: '#E0E0E0',
    cursor: 'not-allowed',
    '&:hover': {
      backgroundColor: 'transparent'
    }
  },
  '& .react-datepicker__triangle': {
    display: 'none'
  }
};

const assignmentFormFieldLabelStyles = {
  fontWeight: mixins.fontWeight500,
  fontSize: mixins.fontSize16,
  color: mixins.lightTextColor2,
  lineHeight: '20px',
  paddingBottom: 4
};

export const DatePickerPortalWrapper = styled(Box)({
  '&&': {
    ...calendarStyles
  }
});

export const AssignmentFormDatePickerPortalWrapper = styled(Box)({
  '&&': {
    maxWidth: '100%',
    ...getAssignmentDueDateCalendarStyles({ withShadow: true, forAssignmentForm: true })
  }
});

export const Label = styled(Typography)({
  '&&': {
    fontWeight: mixins.fontWeight400,
    fontSize: 14,
    fontFamily: mixins.fontFamilyPlusSans,
    color: mixins.greyScaleColor,
    lineHeight: '22px'
  },
  [`${ASSIGNMENT_FORM_DATE_FIELD} &&`]: assignmentFormFieldLabelStyles,
  [`${QUICK_SCHEDULE_FORM_DATE_FIELD} &&`]: quickScheduleFormFieldLabelStyles(mixins)
});

export const DateWrapper = styled(Box)({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    '& .react-datepicker-wrapper': {
      width: '100%',
      height: '100%'
    },
    '& .react-datepicker__input-container': {
      width: '100%',
      height: '100%'
    }
  },
  [`&&${ASSIGNMENT_FORM_DATE_FIELD}`]: {
    width: '100%'
  },
  [`&&${QUICK_SCHEDULE_FORM_DATE_FIELD}`]: {
    width: '100%'
  }
});

export const DatePickerContainer = styled(Box)({
  '&&': {
    position: 'relative',
    width: 144,
    height: 48
  },
  [`${ASSIGNMENT_FORM_DATE_FIELD} &&`]: {
    width: '100%'
  },
  [`${QUICK_SCHEDULE_FORM_DATE_FIELD} &&`]: {
    width: '100%',
    height: 48
  }
});

export const datePickerPopperClassName = 'aet-date-picker-popper';

export const DatePickerPopperStyles = {
  [`&.${datePickerPopperClassName}`]: {
    zIndex: '9999 !important'
  }
};

export const DateInput = styled('input', {
  shouldForwardProp: (prop) => prop !== 'hasSuccess'
})({
  '&&': {
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    borderRadius: 4,
    padding: '12px 40px 12px 12px',
    backgroundColor: '#FFFFFF',
    border: `1px solid ${mixins.amethyst}`,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight400,
    fontFamily: mixins.fontFamilyPlusSans,
    color: '#2E2E2E',
    outline: 'none',
    cursor: 'pointer',
    '&::placeholder': {
      color: '#9CA3AF',
      opacity: 1
    },
    '&:focus': {
      borderColor: mixins.amethyst
    }
  },
  [`${QUICK_SCHEDULE_FORM_DATE_FIELD} &&`]: {
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    borderRadius: 4,
    padding: '12px 40px 12px 12px',
    backgroundColor: mixins.chalkWhiteFont,
    border: `1px solid ${mixins.amethyst}`,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight400,
    fontFamily: mixins.fontFamilyPlusSans,
    color: mixins.greyScaleColor,
    '&::placeholder': {
      color: mixins.greyScaleColor,
      opacity: 1
    }
  }
});

export const DateInputError = styled(DateInput)({
  '&&&&': {
    border: `2px solid ${mixins.accentRedDark}`,
    '&:focus': {
      borderColor: mixins.accentRedDark
    }
  }
});

export const CalendarIcon = styled(Button)({
  '&&': {
    position: 'absolute',
    right: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    pointerEvents: 'none',
    zIndex: 1,
    minWidth: 'unset',
    '& svg': {
      fontSize: 18,
      color: '#2E2E2E'
    }
  },
  [`${QUICK_SCHEDULE_FORM_DATE_FIELD} &&`]: {
    '& svg': {
      fontSize: 16,
      width: 16,
      height: 16,
      color: mixins.color_text_primary
    }
  }
});

export const CustomHeader = styled(Box)({
  '&&': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 12px',
    marginBottom: 8
  }
});

export const NavButton = styled(Button)({
  '&&': {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: 16,
    padding: '4px 8px',
    color: mixins.amethyst,
    minWidth: 32,
    transition: 'color 0.2s ease',
    textTransform: 'none',
    '&:hover': {
      color: mixins.amethyst,
      background: 'none'
    },
    '&:focus': {
      outline: 'none',
      color: mixins.amethyst
    },
    '&:disabled': {
      color: '#E0E0E0',
      cursor: 'not-allowed',
      '&:hover': {
        backgroundColor: 'transparent'
      }
    }
  }
});

export const MonthYearLabel = styled('span')({
  '&&': {
    fontSize: 16,
    fontWeight: 600,
    color: '#2E2E2E',
    fontFamily: mixins.fontFamilyPlusSans,
    flex: 1,
    textAlign: 'center'
  }
});

export const AssignmentFormCustomHeader = styled(Box)({
  '&&': {
    ...assignmentCalendarHeaderStyles.customHeader
  }
});

export const AssignmentFormNavGroup = styled(Box)({
  '&&': {
    ...assignmentCalendarHeaderStyles.navGroup
  }
});

export const AssignmentFormNavButton = styled(Button)({
  '&&': {
    ...assignmentCalendarHeaderStyles.navButton,
    textTransform: 'none',
    '&:hover': {
      background: 'none'
    }
  }
});

export const AssignmentFormMonthYearLabel = styled('span')({
  '&&': {
    ...assignmentCalendarHeaderStyles.monthYearLabel
  }
});

export const DatePickerPortalGlobalStyles = {
  '#react-datepicker-portal': { zIndex: '9999 !important' },
  '.aet-date-picker-popper': { zIndex: '9999 !important' }
};
